import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MessageSquare, Send, Lock } from "lucide-react";

function tag(role) {
  if (role === "tech_director" || role === "club_rep" || role === "admin") return "TD";
  if (role === "head_scout" || role === "scout") return "Scout";
  if (role === "head_coach" || role === "asst_coach") return "Coach";
  if (role === "club_doctor" || role === "physio") return "Medical";
  return role;
}

export default function CommentsThread({ recordType, recordId, testid = "comments" }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/comments/${recordType}/${recordId}`).then((r) => setComments(r.data || [])).catch(() => setComments([]));

  useEffect(() => { if (open) load(); }, [open, recordType, recordId]); // eslint-disable-line

  const post = async () => {
    if (!body.trim()) return;
    setLoading(true);
    try {
      await api.post(`/comments/${recordType}/${recordId}`, { body: body.trim() });
      setBody("");
      await load();
    } finally { setLoading(false); }
  };

  return (
    <div className="mt-2" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] font-bold text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1"
        data-testid={`${testid}-toggle`}
      >
        <MessageSquare size={11} /> {open ? "Hide comments" : `Comments${comments.length ? ` (${comments.length})` : ""}`}
      </button>
      {open && (
        <div className="mt-2 border border-slate-200 rounded-xl p-3 bg-slate-50/40">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mb-2">
            <Lock size={9} /> Internal — visible only to same-suite roles and the Director.
          </div>
          <div className="space-y-2 max-h-44 overflow-y-auto pm-scroll mb-2">
            {comments.length === 0 && <div className="text-[11px] text-slate-400">No comments yet — start the thread below.</div>}
            {comments.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-lg px-2.5 py-2" data-testid={`${testid}-item-${c.id}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="pm-pill pm-pill-slate text-[9px]">{tag(c.author_role)}</span>
                  <span className="text-[11px] font-bold text-slate-800 truncate">{c.author}</span>
                  <span className="text-[10px] text-slate-400 ml-auto font-mono-tab">{c.created_at.slice(11, 16)}</span>
                </div>
                <div className="text-[12px] text-slate-700 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <textarea
              className="pm-input flex-1 text-[12px] py-2"
              rows={2}
              placeholder="Add a comment for your team…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid={`${testid}-input`}
            />
            <button
              onClick={post}
              disabled={!body.trim() || loading}
              className="pm-btn-primary h-9 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1 disabled:opacity-50"
              data-testid={`${testid}-send`}
            ><Send size={11} /> {loading ? "Sending…" : "Send"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
